import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { TrendChart } from '../TrendChart';
import type { MetricCard, Report as ReportData, ValueFormat } from '../types';
import '../report.css';

/**
 * A client's report for one account.
 *
 * Public by design: this page is embedded as an iframe in the client's own
 * HubSpot dashboard, so it must render for a reader who has no account here.
 * The token in the URL is the credential; see api/routes/reports.py.
 *
 * Every client gets this same layout. That is the point -- onboarding a
 * client is linking their accounts, not rebuilding a dashboard by hand.
 */

const RANGES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
];

function exact(value: number, format: ValueFormat): string {
  if (format === 'percent') return `${(value * 100).toFixed(1)}%`;
  return new Intl.NumberFormat().format(Math.round(value));
}

/** For axis ticks and per-point labels, where space is tight. */
function short(value: number, format: ValueFormat): string {
  if (format === 'percent') return `${(value * 100).toFixed(1)}%`;
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat().format(Math.round(value));
}

function longDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function Delta({ card }: { card: MetricCard }) {
  if (card.previous === null) {
    return <span className="rp-delta rp-flat">no comparable period</span>;
  }
  if (card.previous === 0) {
    // A percentage change from zero is infinite, which tells nobody anything.
    return card.value === 0 ? (
      <span className="rp-delta rp-flat">no change</span>
    ) : (
      <span className="rp-delta rp-up">new</span>
    );
  }
  const change = (card.value - card.previous) / Math.abs(card.previous);
  if (!Number.isFinite(change)) return null;
  const better = card.invert ? change < 0 : change > 0;
  const flat = Math.abs(change) < 0.005;
  return (
    <span className={`rp-delta ${flat ? 'rp-flat' : better ? 'rp-up' : 'rp-down'}`}>
      <b>
        {flat ? '±' : change > 0 ? '▲' : '▼'} {Math.abs(change * 100).toFixed(0)}%
      </b>
      <em>vs previous period ({exact(card.previous, card.format)})</em>
    </span>
  );
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Rolling presets, plus a year and month to jump to a specific period.
 *
 * The year and month lists come from the report itself and contain only
 * periods that have data, so every choice leads somewhere -- nobody picks a
 * month from before the account was connected and concludes the reporting is
 * broken.
 */
function PeriodPicker({
  report,
  onChange,
}: {
  report: ReportData;
  onChange: (params: Record<string, string>) => void;
}) {
  const { selected, available_periods: available } = report;
  const calendar = selected.kind !== 'rolling';
  const year = selected.year ?? available[0]?.year ?? new Date().getFullYear();
  const months = available.find((p) => p.year === year)?.months ?? [];

  return (
    <div className="rp-period">
      <nav className="rp-ranges" aria-label="Rolling range">
        {RANGES.map((range) => (
          <button
            key={range.days}
            type="button"
            className={!calendar && selected.days === range.days ? 'rp-on' : undefined}
            onClick={() => onChange({ days: String(range.days) })}
          >
            {range.label}
          </button>
        ))}
      </nav>

      <div className="rp-calendar">
        <label>
          <span className="rp-sr">Year</span>
          <select
            value={calendar ? String(year) : ''}
            onChange={(e) =>
              e.target.value
                ? onChange({ year: e.target.value })
                : onChange({ days: String(selected.days ?? 90) })
            }
          >
            <option value="">Rolling</option>
            {available.map((period) => (
              <option key={period.year} value={period.year}>
                {period.year}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="rp-sr">Month</span>
          <select
            value={selected.month ? String(selected.month) : ''}
            disabled={!calendar}
            onChange={(e) =>
              onChange(
                e.target.value
                  ? { year: String(year), month: e.target.value }
                  : { year: String(year) },
              )
            }
          >
            <option value="">Whole year</option>
            {months.map((month) => (
              <option key={month} value={month}>
                {MONTH_NAMES[month - 1]}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

export function Report() {
  const { token = '' } = useParams();
  const [params, setParams] = useSearchParams();

  // The URL is the single source of truth for the period, so a reader can
  // bookmark or embed one month and get that month back.
  const query = new URLSearchParams();
  const urlYear = params.get('year');
  const urlMonth = params.get('month');
  const urlDays = params.get('days');
  if (urlYear) {
    query.set('year', urlYear);
    if (urlMonth) query.set('month', urlMonth);
  } else {
    query.set('days', urlDays || '90');
  }
  const queryKey = query.toString();

  const [report, setReport] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The report is the whole page here, not a panel in the admin console.
    document.body.classList.add('rp-body');
    return () => document.body.classList.remove('rp-body');
  }, []);

  useEffect(() => {
    let live = true;
    setError(null);
    api
      .report(token, new URLSearchParams(queryKey))
      .then((data) => live && setReport(data))
      .catch((err: unknown) =>
        live
          ? setError(
              err instanceof ApiError && err.status === 404
                ? 'This report link is not valid. Ask your agency for a current one.'
                : 'Could not load this report. Please try again shortly.',
            )
          : undefined,
      );
    return () => {
      live = false;
    };
  }, [token, queryKey]);

  useEffect(() => {
    if (report) document.title = `${report.account_name} — social report`;
  }, [report]);

  if (error) return <div className="rp-message">{error}</div>;
  if (!report) return <div className="rp-message">Loading…</div>;

  const accent = report.accent_color ?? undefined;

  return (
    <div
      className="rp"
      style={accent ? ({ '--rp-accent': accent } as React.CSSProperties) : undefined}
    >
      <header className="rp-head">
        <div className="rp-brand">
          {report.logo_url ? (
            <img src={report.logo_url} alt={report.client_name} />
          ) : (
            <span className="rp-clientname">{report.client_name}</span>
          )}
          <div>
            <h1>{report.account_name}</h1>
            <p>
              {report.platform === 'linkedin' ? 'LinkedIn Page' : 'Instagram account'} ·{' '}
              {longDate(report.period.start)} – {longDate(report.period.end)}
            </p>
          </div>
        </div>
        <PeriodPicker report={report} onChange={(next) => setParams(next, { replace: true })} />
      </header>

      <section className="rp-cards">
        {report.cards.map((card) => (
          <article key={card.key}>
            <h2>{card.label}</h2>
            <p className="rp-value" title={exact(card.value, card.format)}>
              {short(card.value, card.format)}
            </p>
            <Delta card={card} />
            <TrendChart
              buckets={report.buckets}
              points={card.points}
              previousPoints={card.previous_points}
              granularity={report.granularity}
              zeroBased={card.zero_based}
              format={(value) => exact(value, card.format)}
              formatShort={(value) => short(value, card.format)}
            />
          </article>
        ))}
      </section>

      <section className="rp-posts">
        <h2>Top posts</h2>
        {report.top_posts.length === 0 ? (
          <p className="rp-empty">No posts with enough reach to rank in this period.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Post</th>
                <th>{report.reach_label}</th>
                <th>Engagement</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {report.top_posts.map((post, i) => (
                <tr key={`${post.published_on}-${i}`}>
                  <td className="rp-nowrap">{longDate(post.published_on)}</td>
                  <td>
                    {post.url ? (
                      <a href={post.url} target="_blank" rel="noreferrer noopener">
                        {post.title}
                      </a>
                    ) : (
                      post.title
                    )}
                  </td>
                  <td className="rp-num">{exact(post.reach, 'number')}</td>
                  <td className="rp-num">{exact(post.engagement, 'number')}</td>
                  <td className="rp-num">
                    {post.engagement_rate === null ? '—' : exact(post.engagement_rate, 'percent')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {report.notes.length > 0 && (
        <section className="rp-notes">
          {report.notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </section>
      )}

      <footer className="rp-foot">
        <span>
          {report.data_through
            ? `Data through ${longDate(report.data_through)}`
            : 'No data collected yet'}
        </span>
        <span>Updated nightly</span>
      </footer>
    </div>
  );
}
